/**
 * V13.33 — Page centrale /legal : hub de l'espace juridique et RGPD.
 *
 * Liste les 10 rubriques du référentiel. Une rubrique publiée est cliquable
 * vers sa page. Une rubrique en `draft` s'affiche grisée avec la mention
 * "En préparation" (contenu invisible du public tant que non publié).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Scale, FileText, Shield, Cookie, Mail, Users, Camera,
  Baby, Bot, ExternalLink, ChevronRight,
} from "lucide-react";

const ICONS = {
  "mentions-legales": Scale,
  "cgu": FileText,
  "rgpd": Shield,
  "cookies": Cookie,
  "contact-droits": Mail,
  "contributeurs": Users,
  "droit-image": Camera,
  "mineurs": Baby,
  "ia": Bot,
  "sources-externes": ExternalLink,
};

export default function LegalIndexPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/legal").then((r) => setItems(r.data.items || [])).finally(() => setLoading(false));
  }, []);

  return (
    <div className="mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8 py-10" data-testid="legal-index">
      <header className="mb-8">
        <h1 className="font-display text-3xl sm:text-4xl font-bold flex items-center gap-2">
          <Scale className="h-7 w-7 text-[hsl(var(--aito-lagoon))]" aria-hidden="true" />
          Informations légales et protection des données
        </h1>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed max-w-2xl">
          Retrouve ici l'ensemble des textes qui encadrent l'usage d'AITO.PF : mentions
          légales, conditions d'utilisation, politique RGPD, chartes et modalités d'exercice
          de tes droits.
        </p>
      </header>

      {loading ? (
        <div className="grid sm:grid-cols-2 gap-3">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {items.map((it) => {
            const Icon = ICONS[it.key] || FileText;
            const isPublished = it.status === "published";
            const inner = (
              <Card
                className={`p-4 h-full flex items-start gap-3 transition-shadow ${
                  isPublished
                    ? "hover:shadow-md cursor-pointer border-border"
                    : "opacity-70 border-dashed border-muted-foreground/30 cursor-default"
                }`}
                data-testid={`legal-entry-${it.key}`}
                data-status={it.status}
              >
                <div className={`shrink-0 h-10 w-10 rounded-lg flex items-center justify-center ${
                  isPublished
                    ? "bg-[hsl(var(--aito-lagoon))]/10 text-[hsl(var(--aito-lagoon))]"
                    : "bg-muted text-muted-foreground"
                }`}>
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="font-display font-semibold text-base leading-tight">{it.title}</h2>
                    {!isPublished && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-200">
                        En préparation
                      </span>
                    )}
                  </div>
                  {isPublished && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Version {it.version}
                      {it.updated_at && ` · maj ${new Date(it.updated_at).toLocaleDateString("fr-FR")}`}
                    </p>
                  )}
                </div>
                {isPublished && <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-2" aria-hidden="true" />}
              </Card>
            );
            return isPublished ? (
              <Link key={it.key} to={`/legal/${it.key}`}>{inner}</Link>
            ) : (
              <div key={it.key}>{inner}</div>
            );
          })}
        </div>
      )}

      <p className="text-xs text-muted-foreground mt-8 italic">
        Les pages en préparation ne sont pas encore ouvertes au public. Une notice sera
        publiée dès que chaque texte sera finalisé.
      </p>
    </div>
  );
}
